/**
 * Préparation d'images avant envoi (photo d'étiquette de réception).
 *
 * Deux usages distincts, d'où deux réglages :
 * - prepareImageForUpload : archivage S3 de la photo (traçabilité bio) — on garde
 *   une bonne définition, et on ne recompresse pas un fichier déjà petit.
 * - downscaleForVision : lecture DLC/lot par OCR — le poids compte (cap serveur
 *   ~5 Mo, et coût en tokens quand le repli cloud est utilisé).
 *
 * Les deux tolèrent l'échec : un format que le navigateur ne sait pas décoder
 * (certains HEIC) part tel quel plutôt que de bloquer la réception.
 */

/**
 * Réduit et recompresse en JPEG avant l'envoi S3. Une photo de smartphone récente
 * dépasse facilement la limite d'upload (10 Mo) : multer coupe alors le flux et
 * l'écran affiche « Multipart: Unexpected end of form » (constaté le 30/07/2026 à
 * la réception du lait). 2400 px suffisent largement pour lire une étiquette.
 * Renvoie le fichier d'origine si la réduction est inutile ou impossible.
 */
export function prepareImageForUpload(
  file: File,
  maxDim = 2400,
  quality = 0.85,
  /** En dessous de ce poids, le fichier part tel quel. 0 = toujours réduire. */
  seuilOctets = 2 * 1024 * 1024,
): Promise<File> {
  return new Promise((resolve) => {
    if (file.size <= seuilOctets) return resolve(file);
    try {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        URL.revokeObjectURL(url);
        if (!ctx) return resolve(file);
        // Fond blanc AVANT de dessiner : JPEG n'a pas de transparence, et un PNG
        // détouré sortirait sur fond NOIR — le défaut que le serveur corrige
        // déjà par `flatten` (logo Nature & Progrès, 27/08/2026). Sans effet sur
        // une photo opaque.
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);
        canvas.toBlob(
          (blob) => {
            if (!blob || blob.size >= file.size) return resolve(file);
            resolve(new File([blob], file.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' }));
          },
          'image/jpeg',
          quality,
        );
      };
      img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
      img.src = url;
    } catch {
      resolve(file);
    }
  });
}

/**
 * Réduit et encode en base64 JPEG (sans préfixe data:) pour l'OCR — le bridge
 * limite le body JSON à 10 Mo. 2600 px / q0.8 : l'OCR local (Apple Vision) n'a
 * aucun coût par token, donc on garde de quoi lire les petites DLC imprimées,
 * sans jamais upscaler (Math.min(1, …)). La photo S3 reste en pleine résolution.
 * Chaîne vide si le fichier est illisible.
 */
export function downscaleForVision(file: File, maxDim = 2600, quality = 0.8): Promise<string> {
  return new Promise((resolve) => {
    const readRaw = () => {
      const reader = new FileReader();
      reader.onload = () => {
        const res = reader.result as string;
        resolve(res.includes(',') ? res.split(',')[1] : res);
      };
      reader.onerror = () => resolve('');
      reader.readAsDataURL(file);
    };
    try {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        URL.revokeObjectURL(url);
        if (!ctx) return readRaw();
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/jpeg', quality).split(',')[1] || '');
      };
      img.onerror = () => { URL.revokeObjectURL(url); readRaw(); };
      img.src = url;
    } catch {
      readRaw();
    }
  });
}

/**
 * Lecture DLC + n° de lot d'une photo d'étiquette, best-effort.
 * 1) bridge local (OCR Apple Vision, gratuit, ~instantané) sur les Mac mini ;
 * 2) repli cloud (Claude Vision) — indispensable sur iPad/iPhone, qui n'ont pas de bridge.
 * Renvoie null si les deux échouent : la réception n'est jamais bloquée.
 */
export async function readLabel(
  file: File,
  local: (base64: string) => Promise<LabelInfo | null>,
  cloud: (base64: string, mime: string) => Promise<Omit<LabelInfo, 'source'>>,
): Promise<LabelInfo | null> {
  try {
    const base64 = await downscaleForVision(file);
    if (!base64) return null;
    const localInfo = await local(base64);
    if (localInfo) return localInfo;
    try {
      // downscaleForVision ré-encode toujours en JPEG via canvas.
      const cloudInfo = await cloud(base64, 'image/jpeg');
      return cloudInfo ? { ...cloudInfo, source: 'cloud' } : null;
    } catch {
      return null;
    }
  } catch {
    return null;
  }
}

export interface LabelInfo {
  dlc: string | null;
  lot_number: string | null;
  date_type: 'DLC' | 'DDM' | null;
  confidence: number;
  source: 'local' | 'cloud';
}
